import React, { useState } from "react";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme";
import {
  ExitToApp,
  LockOutlined,
  PersonOutlined,
  OpenInNew,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom"; // for redirect
import SystemAlert from "./SystemAlert";
import ProgressIndicator from "./ProgressIndicator";
import { useDispatch } from "react-redux";
import { logout } from "../../utils/auth/logoutUtility";
const apps = [
  {
    name: "ESKAIY POS",
    description: "Point of Sales Software",
    logo: "https://storage.googleapis.com/a1aa/image/Y-zqZDr04s0vrd6jDPQfW_MGq2vA1zkTjwcgEZU3Ljg.jpg",
  },
  {
    name: "ESKAIY RES",
    description: "Restaurant Management Software",
    logo: "https://storage.googleapis.com/a1aa/image/MmCcbJfLQYNpfiHNzwgs9UKXjdIzzE1MsdspN8CbmZ4.jpg",
  },
  {
    name: "ESKAIY INVENTORY",
    description: "Inventory Management Software",
    logo: "https://storage.googleapis.com/a1aa/image/TMhsfHFnD21TFaij6iDI5h3qn-b6S_s-oNzL94mSiJk.jpg",
  },
  {
    name: "ESKAIY PMS",
    description: "Property Management Software",
    logo: "https://storage.googleapis.com/a1aa/image/_geXj7xAoZnzrJ7iqdam03xAv4JtjUswYS4qkKNSByM.jpg",
  },
];

const menuItems = [
  { name: "Profile", icon: <PersonOutlined /> },
  { name: "Change Password", icon: <LockOutlined /> },
  { name: "Log Out", icon: <ExitToApp /> },
];
function Profile() {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [selectedApp, setSelectedApp] = useState(apps[0].name);
  const [selectedMenuItem, setSelectedMenuItem] = useState("");
  const dispatch = useDispatch();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  // Progress tracking states
  const [progressOpen, setProgressOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState("");
  const [completedSteps, setCompletedSteps] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const navigate = useNavigate(); // for navigation

  const handleLogout = async () => {
    try {
      // Start logging out
      setIsLoggingOut(true);
      setProgressOpen(true);
      setProgress(0);
      setCompletedSteps([]);

      // Show logout message
      setSnackbar({
        open: true,
        message: "Logging out...",
        type: "info",
      });

      // Perform comprehensive logout with hard reload and progress tracking
      // Note: logout() will perform hard reload, so the code below won't execute
      await logout({
        preserveTheme: true,
        onProgress: (step, progressValue, completed) => {
          setCurrentStep(step);
          setProgress(progressValue);
          setCompletedSteps([...completed]);
        },
      });

      // The following code won't execute due to hard reload, but keeping for fallback
      setSnackbar({
        open: true,
        message: "Logged Out Successfully!",
        type: "success",
      });
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      // Fallback: force navigation if logout fails
      setSnackbar({
        open: true,
        message: "Logout completed. Redirecting...",
        type: "warning",
      });
      setProgressOpen(false);
      setIsLoggingOut(false);
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 1000);
    }
  };

  return (
    <Box
      sx={{
        width: 380,
        bgcolor: "background.paper",
        borderRadius: 1,
        boxShadow: 4,
        overflow: "hidden",
        p: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Avatar
          alt="User avatar"
          src="https://storage.googleapis.com/a1aa/image/sEOZWreQ55qwQmRvi1gGJdVCTxq404PM_Rpw9wDBMug.jpg"
          sx={{ width: 48, height: 48 }}
        />
        <Box sx={{ ml: 2, display: "flex", flexDirection: "column" }}>
          <Typography variant="subtitle_m" color="text.primary">
            John Doe
          </Typography>
          <Typography variant="body_xs" color="text.secondary">
            Admin
          </Typography>
        </Box>
        <Box
          sx={{
            ml: "auto",
            textAlign: "right",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="body_xs" color="text.secondary">
            Default Location
          </Typography>
          <Typography variant="button_s" color="primary">
            Inventory Colombo
          </Typography>
        </Box>
      </Box>
      <Typography variant="subtitle_s" color="text.secondary" gutterBottom>
        Your ESKAIY Apps
      </Typography>
      <Box
        sx={{
          mb: 2,
          maxHeight: 300,
          overflowY: "auto",
          backgroundColor: theme.palette.background.default,
          p: 1,
          borderRadius: 1,
          // overflow: "hidden",
        }}
      >
        {apps.map((app) => (
          <Box
            key={app.name}
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor:
                selectedApp === app.name
                  ? theme.palette.primary.main
                  : theme.palette.background.paper,
              boxShadow: `0px 1px 2px rgba(0, 0, 0, 0.1)`,

              p: 2,
              borderRadius: 1,
              mb: 1,
              cursor: "pointer",
              "&:hover": {
                bgcolor: "blue.50",
              },
            }}
            onClick={() => setSelectedApp(app.name)}
          >
            <Avatar
              alt={`${app.name} logo`}
              src={app.logo}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ ml: 2, ...styles.flex_direction_column }}>
              <Typography
                variant="button_s"
                sx={{
                  color:
                    selectedApp === app.name
                      ? theme.palette.greyscale.white
                      : theme.palette.text.primary,
                }}
                // color="text.primary"
              >
                {app.name}
              </Typography>
              <Typography
                variant="body_xs"
                sx={{
                  color:
                    selectedApp === app.name
                      ? theme.palette.greyscale.white
                      : theme.palette.text.secondary,
                }}
                // color="text.secondary"
              >
                {app.description}
              </Typography>
            </Box>
            <IconButton
              sx={{
                ml: "auto",
                color:
                  selectedApp === app.name
                    ? theme.palette.greyscale.white
                    : theme.palette.text.primary,
              }}
            >
              <OpenInNew />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.name}
            sx={{
              transition: "all 0.3s ease-in-out",

              backgroundColor:
                selectedMenuItem === item.name
                  ? theme.palette.secondary.main
                  : "inherit",
              "&:hover": {
                backgroundColor: theme.palette.tertiary.main,
                color: theme.palette.primary.main,
                borderRight: `3px solid ${theme.palette.secondary.main}`,
              },

              boxShadow:
                selectedMenuItem === item.name
                  ? `0px 1px 2px rgba(0, 0, 0, 0.1)`
                  : 0,
              color:
                selectedMenuItem === item.name
                  ? theme.palette.button.primaryText
                  : theme.palette.text.primary,
              gap: 2,
              fontSize: "5rem",
            }}
            // onClick={() => setSelectedMenuItem(item.name)}
            onClick={() => {
              setSelectedMenuItem(item.name);
              if (item.name === "Log Out") {
                handleLogout();
              }
            }}
          >
            {item.icon}
            <ListItemText
              primary={item.name}
              primaryTypographyProps={{
                fontSize: theme.typography.body_s,
              }}
            />
          </ListItem>
        ))}
      </List>
      <SystemAlert
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
      <ProgressIndicator
        open={progressOpen}
        currentStep={currentStep}
        completedSteps={completedSteps}
        progress={progress}
        status={isLoggingOut ? "loading" : "success"}
      />
    </Box>
  );
}

export default Profile;
